/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ProductCode } from '~/helpers/LwinUtility';
import { useRouter } from 'next/router';
import { tradeRoutes } from '~/routes/trade';

export interface ModalRouterContextContent {
    value?: {
        productCode: ProductCode;
        productId: number;
        portfolioId: string;
    };
    event: any;
    open: boolean;
    handleModalData: (data?: ModalRouterContextContent['value']) => void;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const initialModalRouterContext: ModalRouterContextContent = {
    event: null,
    open: false,
    handleModalData: (): void => {},
    setOpen: (): void => {}
};

export const ModalRouterContext = createContext(initialModalRouterContext);

ModalRouterContext.displayName = 'ModalRouterContext';

/**
 * Context holds contextual navigation items for modal router.
 */
export const ModalRouterContextProvider: React.FC<{ children?: ReactNode }> = ({ children }) => {
    const context = useContext(ModalRouterContext);
    const [value, setValue] = useState(context);
    const [open, setOpen] = useState(false);
    const router = useRouter();
    const { query } = router;

    const handleModalData = useCallback(
        (modalData?: ModalRouterContextContent['value']) => {
            if (!modalData) {
                void router.push(router.asPath, router.asPath, {
                    shallow: true
                });
                return;
            }
            // keep the push so the back button works but no longer a standalone page
            const path = `${tradeRoutes.marketplace.href}?portfolioId=${modalData.portfolioId}&productCode=${modalData.productCode.value}&productId=${modalData.productId}`;
            void router.push(path, path, {
                shallow: true
            });
        },
        [router]
    );

    const contextValue: ModalRouterContextContent = useMemo(() => {
        return { ...value, open, handleModalData };
    }, [handleModalData, open, value]);

    useEffect(() => {
        if (!query.portfolioId || !query.productCode || !query.productId) {
            if (open) {
                setOpen(false);
                setValue({ ...value, value: undefined });
            }
        }
        if (!open && query.portfolioId && query.productCode && query.productId) {
            const portfolioId = query.portfolioId as string;
            const productCode = new ProductCode(query.productCode as string);
            const productId = parseInt(query.productId as string, 10);
            setValue({ ...value, value: { portfolioId, productCode, productId } });
            setOpen(true);
        }
    }, [query, open, setOpen, value]);

    return <ModalRouterContext.Provider value={contextValue}>{children}</ModalRouterContext.Provider>;
};
